<script setup>
import { computed, ref } from 'vue';
import { useElementSize } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import ResizableEditorWrapper from 'dashboard/components/widgets/conversation/ResizableEditorWrapper.vue';
import StaffChatMessageList from './StaffChatMessageList.vue';
import StaffChatReplyBox from './StaffChatReplyBox.vue';
import StaffChatContextMenuHost from './StaffChatContextMenuHost.vue';

const props = defineProps({
  chat: {
    type: Object,
    default: () => ({}),
  },
  isOnExpandedLayout: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['chatDeleted']);

const { t } = useI18n();
const currentUser = useMapGetter('getCurrentUser');

const otherUser = computed(() => props.chat?.meta?.otherUser);
const hasChat = computed(() => Boolean(props.chat?.id));
const isGroupChat = computed(() => props.chat?.chatType === 'group');
const participants = computed(() => props.chat?.meta?.participants || []);
const chatTitle = computed(() => {
  if (props.chat?.displayName) return props.chat.displayName;
  if (isGroupChat.value)
    return props.chat?.name || t('STAFF_CHAT.NEW_GROUP_CHAT');
  return otherUser.value?.name;
});
const membersSubtitle = computed(() => {
  if (!isGroupChat.value) return otherUser.value?.email;
  return t('STAFF_CHAT.MEMBERS_COUNT', { count: participants.value.length });
});

const staffChatBoxRef = ref(null);
const resizableEditorWrapperRef = ref(null);
const { height: containerHeight } = useElementSize(staffChatBoxRef);

const toggleReplyEditorSize = () => {
  resizableEditorWrapperRef.value?.toggleEditorExpand?.();
};
</script>

<template>
  <div
    ref="staffChatBoxRef"
    class="flex flex-col flex-1 min-w-0 h-full bg-n-surface-1"
    :class="{
      'border-l rtl:border-l-0 rtl:border-r border-n-weak': !isOnExpandedLayout,
    }"
  >
    <template v-if="hasChat">
      <StaffChatContextMenuHost :chat="chat" @deleted="emit('chatDeleted')">
        <template #default="{ openContextMenu }">
          <header
            class="flex items-center gap-3 px-4 h-[3.25rem] border-b border-n-weak shrink-0"
            @contextmenu="openContextMenu"
          >
            <Avatar
              v-if="!isGroupChat"
              :name="otherUser?.name"
              :src="otherUser?.thumbnail"
              :size="32"
              rounded-full
            />
            <div
              v-else
              class="flex items-center justify-center rounded-full size-8 bg-n-slate-3 text-n-slate-11"
            >
              <span class="text-base i-lucide-users" />
            </div>
            <div class="flex-1 min-w-0">
              <h2 class="text-sm font-medium truncate text-n-slate-12">
                {{ chatTitle }}
              </h2>
              <p
                v-if="membersSubtitle"
                class="text-xs truncate text-n-slate-11"
              >
                {{ membersSubtitle }}
              </p>
            </div>
          </header>
        </template>
      </StaffChatContextMenuHost>
      <StaffChatMessageList
        :messages="chat.messages || []"
        :current-user-id="currentUser?.id"
        :is-group-chat="isGroupChat"
      />
      <div class="flex relative flex-col shrink-0 bg-n-surface-1">
        <ResizableEditorWrapper
          ref="resizableEditorWrapperRef"
          :container-height="containerHeight"
        >
          <StaffChatReplyBox
            :chat-id="chat.id"
            :is-on-expanded-layout="isOnExpandedLayout"
            @toggle-editor-size="toggleReplyEditorSize"
          />
        </ResizableEditorWrapper>
      </div>
    </template>
    <div
      v-else
      class="flex flex-col items-center justify-center flex-1 gap-2 px-6 text-center"
    >
      <span class="text-4xl i-lucide-messages-square text-n-slate-9" />
      <h2 class="text-lg font-medium text-n-slate-12">
        {{ t('STAFF_CHAT.EMPTY_STATE.TITLE') }}
      </h2>
      <p class="text-sm text-n-slate-11">
        {{ t('STAFF_CHAT.EMPTY_STATE.SUBTITLE') }}
      </p>
    </div>
  </div>
</template>
